import { useState, useMemo, useId } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useUIStore } from '@/store/uiStore';

export interface ExerciseStatsData {
  totalWorkouts: number;
  overallMaxWeight?: number;
  overallMaxDate?: string;
  overallMaxExerciseNumber?: number;
  history: Array<{
    date: string;
    sets: number;
    maxWeight?: number;
    totalReps: number;
    totalVolume?: number;
    elapsedSeconds?: number;
    exerciseNumberInWorkout?: number;
    avgDifficulty: number;
    details: Array<{
      setNumber: number;
      reps: number;
      weight: number;
      difficulty: number;
    }>;
  }>;
}

interface ExerciseStatsPanelProps {
  stats: ExerciseStatsData;
  onClose: () => void;
  stopPropagation?: boolean;
  emptyMessage?: string;
}

const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${seconds}mp`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return s > 0 ? `${m}p ${s}mp` : `${m}p`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm > 0 ? `${h}ó ${rm}p` : `${h}ó`;
};

const formatDurationShort = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return s > 0 ? `${m}:${String(s).padStart(2, '0')}` : `${m}p`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return `${h}:${String(rm).padStart(2, '0')}`;
};

export const ExerciseStatsPanel = ({
  stats,
  onClose,
  stopPropagation = false,
  emptyMessage = 'Még nincs edzésed ezzel a géppel.',
}: ExerciseStatsPanelProps) => {
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(0);
  const [hoveredChartIndex, setHoveredChartIndex] = useState<number | null>(null);
  const [showChart, setShowChart] = useState(true);
  const { darkMode } = useUIStore();
  const gradientId = useId().replace(/:/g, '_');

  const wrap = (handler: () => void) => {
    if (!stopPropagation) return handler;
    return (e: React.MouseEvent) => {
      e.stopPropagation();
      handler();
    };
  };

  const currentWorkout = stats.history[currentHistoryIndex];
  const hasWeightData = stats.history.some(h => h.maxWeight !== undefined && h.maxWeight > 0);
  const hasDurationData = !hasWeightData && stats.history.some(h => h.elapsedSeconds !== undefined && h.elapsedSeconds > 0);

  const handlePrevHistory = () => {
    if (currentHistoryIndex < stats.history.length - 1) {
      setCurrentHistoryIndex(currentHistoryIndex + 1);
    }
  };

  const handleNextHistory = () => {
    if (currentHistoryIndex > 0) {
      setCurrentHistoryIndex(currentHistoryIndex - 1);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatChartDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' });
  };

  // Chart: single line – weight (if available) or duration (if no weight)
  const chartData = useMemo(() => {
    if (stats.history.length < 2) return null;

    const sorted = [...stats.history].reverse();

    let values: number[];
    let mode: 'weight' | 'duration';

    if (hasWeightData) {
      mode = 'weight';
      values = sorted.map(h => h.maxWeight ?? 0);
    } else if (hasDurationData) {
      mode = 'duration';
      values = sorted.map(h => h.elapsedSeconds ?? 0);
    } else {
      return null;
    }

    if (values.every(v => v === 0)) return null;

    const maxVal = Math.max(...values);
    const minVal = 0;

    const width = 100;
    const height = 50;
    const pad = 5;
    const chartH = height - 2 * pad;

    const points = sorted.map((_, i) => {
      const x = pad + (i / (sorted.length - 1)) * (width - 2 * pad);
      const y = height - pad - (values[i] / maxVal) * chartH;
      return { x, y };
    });

    const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    const areaD = `${pathD} L ${points[points.length - 1].x} ${height - pad} L ${points[0].x} ${height - pad} Z`;

    return {
      sorted, values, mode, minVal, maxVal, points, pathD, areaD,
    };
  }, [stats, hasWeightData, hasDurationData]);

  if (stats.totalWorkouts === 0) {
    return (
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500 dark:text-gray-400">{emptyMessage}</p>
        <button type="button" onClick={wrap(onClose) as any} className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded">
          <X className="w-4 h-4 text-gray-400" />
        </button>
      </div>
    );
  }

  const lineColor = hasWeightData ? '#3b82f6' : '#f59e0b';
  const labelUnit = hasWeightData ? 'kg' : '';

  return (
    <>
      {/* Header with navigation */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={wrap(handlePrevHistory) as any}
            disabled={currentHistoryIndex >= stats.history.length - 1}
            className="p-1 rounded hover:bg-white dark:hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          </button>
          <div className="text-center min-w-[100px]">
            <div className="text-xs font-medium text-gray-900 dark:text-gray-100">
              {currentWorkout && formatDate(currentWorkout.date)}
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400">
              {currentHistoryIndex + 1} / {stats.history.length} edzés
            </div>
          </div>
          <button
            type="button"
            onClick={wrap(handleNextHistory) as any}
            disabled={currentHistoryIndex <= 0}
            className="p-1 rounded hover:bg-white dark:hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          </button>
        </div>

        <div className="flex items-center gap-2">
          {/* Max weight badge - only if weight data exists */}
          {hasWeightData && currentWorkout?.maxWeight !== undefined && (
            <div className="bg-primary-100 dark:bg-primary-900/50 px-2 py-1 rounded">
              <span className="text-xs text-primary-600 dark:text-primary-400">Max: </span>
              <span className="text-sm font-bold text-primary-700 dark:text-primary-300">{currentWorkout.maxWeight} kg</span>
              {currentWorkout.exerciseNumberInWorkout !== undefined && (
                <span className="text-[10px] text-primary-600 dark:text-primary-400 ml-1">
                  · {currentWorkout.exerciseNumberInWorkout}. gyakorlat
                </span>
              )}
            </div>
          )}
          {/* Duration badge - only if duration mode */}
          {hasDurationData && currentWorkout?.elapsedSeconds !== undefined && currentWorkout.elapsedSeconds > 0 && (
            <div className="bg-amber-100 dark:bg-amber-900/50 px-2 py-1 rounded">
              <span className="text-xs text-amber-600 dark:text-amber-400">Idő: </span>
              <span className="text-sm font-bold text-amber-700 dark:text-amber-300">{formatDuration(currentWorkout.elapsedSeconds)}</span>
            </div>
          )}
          <button type="button" onClick={wrap(onClose) as any} className="p-1 hover:bg-white dark:hover:bg-gray-600 rounded">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      </div>

      {/* All-time max */}
      {hasWeightData && stats.overallMaxWeight !== undefined && stats.overallMaxWeight > 0 && (
        <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-2 bg-gray-50 dark:bg-gray-800/50 rounded px-2 py-1.5 border border-gray-200 dark:border-gray-600">
          <span className="font-medium text-gray-700 dark:text-gray-200">Abszolút max (összes edzés): </span>
          <span className="font-semibold text-primary-600 dark:text-primary-400">{stats.overallMaxWeight} kg</span>
          {stats.overallMaxDate && (
            <span className="ml-1">
              · {formatDate(stats.overallMaxDate)}
            </span>
          )}
          {stats.overallMaxExerciseNumber !== undefined && stats.overallMaxExerciseNumber > 0 && (
            <span className="ml-1">
              · {stats.overallMaxExerciseNumber}. gyakorlatként
            </span>
          )}
          {stats.totalWorkouts > stats.history.length && (
            <span className="block mt-0.5 text-gray-400">
              ({stats.totalWorkouts} edzés összesen, az utolsó {stats.history.length} lapozható)
            </span>
          )}
        </div>
      )}

      {/* Sets table */}
      {currentWorkout && currentWorkout.details.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600 overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                <th className="py-1.5 px-2 text-left font-medium text-gray-600 dark:text-gray-300">Sor</th>
                <th className="py-1.5 px-2 text-center font-medium text-gray-600 dark:text-gray-300">Ismétlés</th>
                {hasWeightData && (
                  <th className="py-1.5 px-2 text-center font-medium text-gray-600 dark:text-gray-300">Súly</th>
                )}
                <th className="py-1.5 px-2 text-center font-medium text-gray-600 dark:text-gray-300">Nehézség</th>
              </tr>
            </thead>
            <tbody>
              {currentWorkout.details.map((set, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'}>
                  <td className="py-1.5 px-2 text-gray-700 dark:text-gray-300">{set.setNumber}.</td>
                  <td className="py-1.5 px-2 text-center font-medium text-gray-900 dark:text-gray-100">{set.reps} db</td>
                  {hasWeightData && (
                    <td className="py-1.5 px-2 text-center font-medium text-gray-900 dark:text-gray-100">{set.weight} kg</td>
                  )}
                  <td className="py-1.5 px-2 text-center">
                    <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-semibold difficulty-${set.difficulty}`}>
                      {set.difficulty}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary row */}
      <div className="flex items-center justify-between mt-2 text-[10px] text-gray-500 dark:text-gray-400">
        <span>{currentWorkout?.sets} sorozat · {currentWorkout?.totalReps} ismétlés</span>
        <span>Átlag nehézség: {currentWorkout?.avgDifficulty}</span>
      </div>

      {/* Trend Chart – single line: weight or duration */}
      {chartData && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={wrap(() => setShowChart(!showChart)) as any}
              className="text-[10px] font-medium text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              {showChart ? 'Trend grafikon ▾' : 'Trend grafikon ▸'}
            </button>
            {showChart && (
              <div className="flex items-center gap-3 text-[10px]">
                <span className="flex items-center gap-1">
                  <span className="inline-block w-2.5 h-0.5 rounded" style={{ backgroundColor: lineColor }}></span>
                  <span className="text-gray-500 dark:text-gray-400">
                    {chartData.mode === 'weight' ? 'Max súly' : 'Időtartam'}
                  </span>
                </span>
              </div>
            )}
          </div>

          {showChart && (
            <>
              <div className="relative w-full aspect-[2.5/1]">
                <svg
                  viewBox="0 0 100 50"
                  className="w-full h-full"
                  preserveAspectRatio="none"
                >
                  {/* Grid lines */}
                  <line x1="5" y1="5" x2="95" y2="5" stroke={darkMode ? '#374151' : '#e5e7eb'} strokeWidth="0.3" />
                  <line x1="5" y1="17" x2="95" y2="17" stroke={darkMode ? '#374151' : '#e5e7eb'} strokeWidth="0.2" strokeDasharray="1,1" />
                  <line x1="5" y1="27.5" x2="95" y2="27.5" stroke={darkMode ? '#374151' : '#e5e7eb'} strokeWidth="0.3" />
                  <line x1="5" y1="38" x2="95" y2="38" stroke={darkMode ? '#374151' : '#e5e7eb'} strokeWidth="0.2" strokeDasharray="1,1" />
                  <line x1="5" y1="45" x2="95" y2="45" stroke={darkMode ? '#374151' : '#e5e7eb'} strokeWidth="0.3" />

                  {/* Area fill */}
                  <path d={chartData.areaD} fill={`url(#${gradientId})`} opacity="0.2" />

                  {/* Line */}
                  <path d={chartData.pathD} fill="none" stroke={lineColor} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />

                  {/* Interactive points */}
                  {chartData.points.map((p, i) => (
                    <g key={i}>
                      <circle cx={p.x} cy={p.y} r="4" fill="transparent" className="cursor-pointer"
                        onMouseEnter={() => setHoveredChartIndex(i)}
                        onMouseLeave={() => setHoveredChartIndex(null)}
                        onTouchStart={() => setHoveredChartIndex(i)}
                        onTouchEnd={() => setHoveredChartIndex(null)}
                      />
                      <circle cx={p.x} cy={p.y}
                        r={hoveredChartIndex === i ? 2 : 1} fill={lineColor}
                        className="pointer-events-none transition-all duration-100" />
                    </g>
                  ))}

                  <defs>
                    <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={lineColor} />
                      <stop offset="100%" stopColor={lineColor} stopOpacity="0" />
                    </linearGradient>
                  </defs>
                </svg>

                {/* Y-axis labels */}
                {chartData.mode === 'weight' ? (
                  <>
                    <span className="absolute top-0 left-0 text-[8px] text-blue-500 font-medium">{chartData.maxVal} {labelUnit}</span>
                    <span className="absolute bottom-0 left-0 text-[8px] text-blue-500 font-medium">{chartData.minVal} {labelUnit}</span>
                  </>
                ) : (
                  <>
                    <span className="absolute top-0 left-0 text-[8px] text-amber-500 font-medium">{formatDurationShort(chartData.maxVal)}</span>
                    <span className="absolute bottom-0 left-0 text-[8px] text-amber-500 font-medium">{formatDurationShort(chartData.minVal)}</span>
                  </>
                )}

                {/* Tooltip */}
                {hoveredChartIndex !== null && (
                  <div
                    className="absolute bg-gray-900 dark:bg-gray-700 text-white text-[10px] px-2 py-1.5 rounded shadow-lg pointer-events-none z-10 whitespace-nowrap"
                    style={{
                      left: `${chartData.points[hoveredChartIndex].x}%`,
                      top: `${Math.min(chartData.points[hoveredChartIndex].y * 2, 60)}%`,
                      transform: 'translate(-50%, -120%)',
                    }}
                  >
                    <div className="text-gray-300 mb-0.5">{formatChartDate(chartData.sorted[hoveredChartIndex].date)}</div>
                    <div className="flex items-center gap-1">
                      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: lineColor }}></span>
                      <span className="font-semibold">
                        {chartData.mode === 'weight'
                          ? `${chartData.values[hoveredChartIndex]} kg`
                          : formatDuration(chartData.values[hoveredChartIndex])
                        }
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* X-axis labels */}
              <div className="flex justify-between mt-0.5 text-[8px] text-gray-400">
                <span>{formatChartDate(chartData.sorted[0].date)}</span>
                {chartData.sorted.length > 2 && (
                  <span>{chartData.sorted.length} edzés</span>
                )}
                <span>{formatChartDate(chartData.sorted[chartData.sorted.length - 1].date)}</span>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};
